import { z } from "zod";
import { Resources, type TFunction } from "Translations/Resources";

const VALIDATION_RESOURCES = Resources.Admin.Campaigns.Form.Validation;

export const getCampaignFormSchema = (t: TFunction) =>
  z
    .object({
      campaignID: z.number().optional(),
      name: z.string().min(1, t(VALIDATION_RESOURCES.NameRequired)).max(100),
      code: z
        .string()
        .min(1, t(VALIDATION_RESOURCES.CodeRequired))
        .max(20)
        .regex(/^[a-z0-9-]+$/, t(VALIDATION_RESOURCES.CodeInvalidFormat)),
      description: z.string().max(500).optional(),
      header: z.string().max(200).optional(),
      linkName: z.string().max(100).optional(),
      linkUrl: z
        .url(t(VALIDATION_RESOURCES.InvalidUrl))
        .max(500)
        .optional()
        .or(z.literal("")),
      successfulPaymentText: z.string().max(1000).optional(),
      color: z
        .string()
        .regex(
          /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/,
          t(VALIDATION_RESOURCES.InvalidColor),
        )
        .max(9)
        .nullable()
        .optional()
        .or(z.literal("")),
      paymentInstructionsText: z
        .string()
        .max(500, t(VALIDATION_RESOURCES.MaxLength, { max: 500 }))
        .nullable()
        .optional()
        .or(z.literal("")),
      onlyWithMooneText: z
        .string()
        .max(500, t(VALIDATION_RESOURCES.MaxLength, { max: 500 }))
        .nullable()
        .optional()
        .or(z.literal("")),
      receiverUserID: z.number().nullable().optional(),
      receiverIdentityUserID: z.number().nullable().optional(),
      isAffiliateEnabled: z.boolean(),
      dateMode: z.enum(["unlimited", "from-to"]),
      startDate: z.string().nullable().optional(),
      endDate: z.string().nullable().optional(),
    })
    .refine(
      ({ dateMode, startDate, endDate }) => {
        if (dateMode === "from-to") {
          return !!startDate && !!endDate;
        }
        return true;
      },
      {
        message: t(VALIDATION_RESOURCES.DateRangeRequired),
        path: ["startDate"],
      },
    )
    .refine(
      ({ dateMode, startDate, endDate }) => {
        if (dateMode === "from-to" && startDate && endDate) {
          return new Date(endDate) >= new Date(startDate);
        }
        return true;
      },
      {
        message: t(VALIDATION_RESOURCES.EndDateAfterStartDate),
        path: ["endDate"],
      },
    );

export type CampaignFormData = z.infer<
  ReturnType<typeof getCampaignFormSchema>
>;
